import { useMemo } from 'react'
import { Props as SelectProps } from 'react-select'
import CustomSelect from '@/components/CustomSelect'
import TitledRecord from '@/types/TitledRecord'

export type TitledRecordsSelectOptionValueType<T extends TitledRecord> = T | T[]

type Option<T extends TitledRecord> = { label: string; value: TitledRecordsSelectOptionValueType<T> }

interface Props<T extends TitledRecord> extends Omit<SelectProps<Option<T>, true>, 'value' | 'options' | 'onChange'> {
  // currently selected record, ex.
  // project.interviews[0] or all
  // project.interviews
  value: TitledRecordsSelectOptionValueType<T>
  options: T[] // all records, ex. project.interviews
  onChange: (value: TitledRecordsSelectOptionValueType<T>) => void
  withAll?: boolean
}

function TitledRecordsSelect<T extends TitledRecord>({
  value,
  options,
  onChange,
  withAll = false,
  isMulti,
  ...rest
}: Props<T>) {
  const allOption: Option<T> = { label: 'All', value: options }
  const customSelectOptions = useMemo<Option<T>[]>(() => {
    const recordOptions = options.map(i => ({ label: i.title, value: i }))
    // when there is only one option, show that option - avoid using allOption
    return withAll && options.length > 1 ? [allOption, ...recordOptions] : recordOptions
  }, [options, withAll])

  const getValue = () => {
    if (Array.isArray(value)) {
      // when there is only one option, show that option - avoid using allOption
      if (options.length > 1 && withAll && value.length === options.length) return allOption
      return customSelectOptions.filter(opt => value.some(v => v.id === (opt.value as TitledRecord).id))
    }

    return customSelectOptions.find(opt => (opt.value as TitledRecord).id === (value as TitledRecord).id)
  }

  return (
    <CustomSelect<Option<T>>
      value={getValue()}
      options={customSelectOptions}
      onChange={(newValue, actionMeta) => {
        if (actionMeta.action === 'select-option') {
          if (actionMeta.option?.label === allOption.label) {
            onChange(allOption.value)
          } else {
            // when isMulti === true
            Array.isArray(newValue)
              // if All was previously selected and a new option added remove All
            ? onChange(newValue?.filter(o => o.label !== allOption.label).flatMap(o => o.value))
            : onChange((newValue as unknown as Option<T>).value)
          }
          return
        }

        // this is reached when isMultiple is 'true' and the action is 'remove-value' or 'clear'
        onChange(newValue.length === 0 ? [] : newValue.flatMap(o => o.value))
      }}
      getOptionValue={opt => Array.isArray(opt.value) ? 'all' : String((opt.value as TitledRecord).id)}
      isSearchable
      isMulti={(isMulti && options.length > 1) || undefined}
      isClearable={options.length > 1}
      {...rest}
    />
  )
}

export default TitledRecordsSelect
